import jsonServerProvider from 'ra-data-json-server';
import { DataProvider, GetListParams, GetManyParams, GetManyReferenceParams } from 'react-admin';
import Keycloak from "keycloak-js";
import { createHttpClient } from '../utils/http-client';
import { fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { ManualReviewParams } from '../models/request/ManualReviewParams';
import { UpdateVerifyStatusParams } from '../models/request/UpdateVerifyStatusParams';
import { RefPriceOrder } from '../models/response/RefPrice';
import { QueryLocationDriversParams } from '../models/request/QueryLocationDriversParams';


function buildUrl(base: string, paths: string[], params: Record<string, any>) {
    const path = paths.join('/');
    const query = stringify(params);
    return `${base}/${path}?${query}`;
}

function extractUsersLoginToken(json: unknown): string | null {
    if (typeof json === 'string') {
        return json;
    }
    if (typeof json === 'object' && json !== null) {
        const record = json as Record<string, unknown>;
        if (typeof record.token === 'string') {
            return record.token;
        }
        if (typeof record.token === 'object' && record.token !== null) {
            const tokenRecord = record.token as Record<string, unknown>;
            if (typeof tokenRecord.token === 'string') {
                return tokenRecord.token;
            }
        }
        const data = record.data;
        if (typeof data === 'object' && data !== null) {
            const dataRecord = data as Record<string, unknown>;
            if (typeof dataRecord.token === 'string') {
                return dataRecord.token;
            }
        }
    }
    return null;
}

interface gybGetListParams extends GetListParams {
    target?: string;
    targetId?: string;
}

export const dataProvider = (keycloak: Keycloak): DataProvider => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8055';
    const baseDataProvider = jsonServerProvider(apiUrl, createHttpClient(keycloak));

    const myDataProvider: DataProvider = {
        ...baseDataProvider,
        getManyReference: async (resource: string, params: GetManyReferenceParams) => {
            const splitResource = resource.split('/');
            const parentPath = splitResource.length > 1 ? splitResource[0] : null;
            const childResource = splitResource.length > 1 ? splitResource[1] : resource;
            const { target, id } = params;
            if (resource && target) {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const query = {
                    ...fetchUtils.flattenObject(params.filter),
                    _sort: field,
                    _order: order,
                    _start: (page - 1) * perPage,
                    _end: page * perPage,
                };
                const url = parentPath
                    ? `${apiUrl}/${parentPath}/${id}/${childResource}?${stringify(query)}`
                    : `${apiUrl}/${childResource}?${stringify(query)}`;
                const { headers, json } = await createHttpClient(keycloak)(url);
                const totalCount = headers ? parseInt(headers.get('x-total-count')?.split('/').pop() || '0', 10) : 0;
                return {
                    data: json,
                    total: totalCount,
                };
            }
            return baseDataProvider.getManyReference(resource, params);
        },
        getOneWithSubResource: async (resource: string, id: string, subResource: string, subId: string) => {
            const url = `${apiUrl}/${resource}/${id}/${subResource}/${subId}`;
            const { json } = await createHttpClient(keycloak)(url);
            return { data: json };
        },
        getMany: async (resource: string, params: GetManyParams) => {
            const url = `${apiUrl}/${resource}/many?${stringify({ ids: params.ids })}`;
            const { json } = await createHttpClient(keycloak)(url);
            return { data: json };
        },
        getList: async (resource: string, params: gybGetListParams) => {
            const { target, targetId, ...otherFilters } = params.filter;
            const paths = [resource, target, targetId].filter(Boolean) as string[];
            const query = {
                ...fetchUtils.flattenObject(otherFilters),
                _sort: params.sort?.field || 'id',
                _order: params.sort?.order || 'ASC',
                _start: (params.pagination?.page ? (params.pagination.page - 1) * params.pagination.perPage : 0),
                _end: (params.pagination?.page ? params.pagination.page * params.pagination.perPage : 10),
            };
            const url = buildUrl(apiUrl, paths, query);
            const { headers, json } = await createHttpClient(keycloak)(url);
            const totalCount = headers ? parseInt(headers.get('x-total-count')?.split('/').pop() || '0', 10) : 0;
            return {
                data: json,
                total: totalCount === 0 ? json.length : totalCount,
            };
        },
        // 发送移动app系统推送消息
        sendPushNotification: async (params: any) => {
            const url = `${apiUrl}/push/mobile-system-push`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        sendSmsNotification: async (params: any) => {
            const url = `${apiUrl}/push/sms-push`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        fetchPushRecord: async (userId: string) => {
            const url = `${apiUrl}/push/push-record/${userId}`;
            const { json } = await createHttpClient(keycloak)(url);
            return { data: json };
        },
        fetchUserInfo: async (userId: string) => {
            const url = `${apiUrl}/users/many?ids=${userId}`;
            const { json } = await createHttpClient(keycloak)(url);
            return { data: json };
        },
        fetchUserLoginToken: async (userId: string) => {
            const url = `${apiUrl}/users/login`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
                body: JSON.stringify({ id: userId }),
            });
            const token = extractUsersLoginToken(json);
            if (!token) {
                throw new Error('无法从 /users/login 响应中解析 token');
            }
            return { data: token };
        },
    
        fetchUserCargoStats: async (userId: string, days: number) => {
            const url = `${apiUrl}/users/cargo-stats/${userId}?days=${days}`;
            const { json } = await createHttpClient(keycloak)(url);
            return json;
        },
        sendManualReview: async (params: ManualReviewParams) => {
            const url = `${apiUrl}/verify/manual`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        // 获取认证照片ID列表
        getVerifyImages: async (verifyId: string) => {
            const url = `${apiUrl}/verify/user/${verifyId}/images`;
            const { json } = await createHttpClient(keycloak)(url);
            return json;
        },
        // 获取认证照片ID列表
        getTruckVerifyImages: async (verifyId: string) => {
            const url = `${apiUrl}/verify/truck/${verifyId}/images`;
            const { json } = await createHttpClient(keycloak)(url);
            return json;
        },
        // 获取认证照片数据
        getVerifyImageData: async (imageId: string) => {
            const url = `${apiUrl}/verify/static/image/${imageId}`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${keycloak.token}`,
                },
            });
            const arrayBuffer = await response.arrayBuffer();
            const base64 = btoa(
                new Uint8Array(arrayBuffer)
                    .reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            return `data:image/jpeg;base64,${base64}`;
        },
        modifyUserStatus: async (verifyId: string, params: UpdateVerifyStatusParams) => {
            const url = `${apiUrl}/verify/user/${verifyId}`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        modifyTruckStatus: async (verifyId: string, params: UpdateVerifyStatusParams) => {
            const url = `${apiUrl}/verify/truck/${verifyId}`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        addCargoPrice: async (params: RefPriceOrder) => {
            const url = `${apiUrl}/cargos/price`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'PUT',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        driversByLocation: async (params: QueryLocationDriversParams) => {
            const url = `${apiUrl}/drivers/by_pos?lat=${params.lat}&lon=${params.lon}&distance=${params.distance}&pos_from=${params.pos_from}&pos_to=${params.pos_to}`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'GET',
            });
            return { data: json };
        },
        querySettlementInfo: async (waybillId: string) => {
            const url = `${apiUrl}/waybills/${waybillId}/checkout`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'GET',
            });
            return { data: json };
        },
        commitSettlementData: async (
            waybillId: string,
            params: { amount: number; weight: number; unit_price: number; single_price?: number | string | null }
        ) => {
            const url = buildUrl(apiUrl, ['waybills', waybillId, 'checkout'], {
                amount: params.amount,
                weight: params.weight,
                unit_price: params.unit_price,
                single_price: params.single_price ?? '',
            });
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
            });
            return { data: json };
        },
        getAlipayBalance: async () => {
            const url = `${apiUrl}/billings/alipay-balance`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'GET',
            });
            return { data: json };
        },
        fetchWaybillTracking: async (params: { order: string; alt?: number; token: string }) => {
            const trackingApiUrl = 'https://trkg.guiyunbao.cn';
            const normalizedTrackingApiUrl = trackingApiUrl.endsWith('/')
                ? trackingApiUrl.slice(0, -1)
                : trackingApiUrl;
            const url = buildUrl(normalizedTrackingApiUrl, ['location', 'tracking'], {
                order: params.order,
                alt: params.alt ?? 1,
            });
            const { json } = await fetchUtils.fetchJson(url, {
                method: 'GET',
                headers: new Headers({
                    Accept: 'application/json',
                    Authorization: `Bearer ${params.token}`,
                    'X-App-Version': '2.3.20',
                }),
            });
            return { data: json };
        },
        resetThirdPartyClientSecret: async (id: string) => {
            const url = `${apiUrl}/thirdPartyClients/${id}/reset-secret`;
            const { json } = await createHttpClient(keycloak)(url, {
                method: 'POST',
            });
            return { data: json };
        },
    };

    return myDataProvider;
};
